<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_FEATURED_IMAGE', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_FEATURED_IMAGE', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <!-- Top part to keep search filters add button and stuff -->
            <div class="flex items-center justify-end mx-auto mb-6">
                <t-button
                    v-if="isSuperAdmin"
                    variant="success"
                    @click="toggleRightSidebar"
                >
                    Add Image
                </t-button>
            </div>

            <!-- Table content to list the Categories -->
            <div
                v-if="featuredImages?.length"
                class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
            >
                <div
                    v-for="featuredImage in featuredImages"
                    :key="'featured-image' + featuredImage.id"
                    class="relative overflow-hidden rounded shadow h-72"
                >
                    <img
                        class="w-full h-full object-stretch"
                        :src="featuredImage.image_path"
                        :alt="featuredImage.title"
                    />

                    <!-- top -->
                    <div class="absolute top-0 w-full h-1/4">
                        <div class="flex items-center justify-end h-full pr-4">
                            <t-toggle
                                :checked="!!featuredImage.published_at"
                                checked-placeholder="Disable"
                                unchecked-placeholder="Publish"
                                checked-label="Published"
                                unchecked-label="Disabled"
                                :classes="{
                                    wrapper: 'bg-gray-200 focus:outline-none focus:shadow-outline rounded-sm p-1',
                                    wrapperChecked:
                                        'bg-gray-200 focus:outline-none focus:shadow-outline rounded-sm p-1',
                                    button: 'rounded-sm bg-red-500 text-white w-20 h-6 bg-white shadow flex items-center justify-center text-xs',
                                    buttonChecked:
                                        'rounded-sm bg-green-500 text-white w-20 h-6 shadow flex items-center justify-center text-xs',
                                    checkedPlaceholder:
                                        'rounded-sm w-20 h-6 flex items-center justify-center text-gray-500 text-xs',
                                    uncheckedPlaceholder:
                                        'rounded-sm w-20 h-6 flex items-center justify-center text-gray-500 text-xs',
                                }"
                                @click="togglePublication(featuredImage)"
                            />
                        </div>
                    </div>

                    <!-- bottom -->
                    <div
                        class="absolute bottom-0 flex items-center justify-between w-full px-3 text-white bg-black bg-opacity-50 h-1/4"
                    >
                        <div>
                            {{ featuredImage.title }}
                        </div>
                        <div class="flex gap-4">
                            <div
                                class="cursor-pointer"
                                @click="edit(featuredImage.id)"
                            >
                                <EditIcon class="w-5 fill-current" />
                            </div>
                            <div
                                class="cursor-pointer"
                                @click="deleteItem(featuredImage.id)"
                            >
                                <DeleteIcon class="w-5 fill-current" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>No Featured Images.</div>

            <RightSidebar
                :is-open="rightSidebar"
                :component-name="rightSidebarComponentName"
                :edit-data="editData"
                @closeRightSidebar="toggleRightSidebar"
            >
                <template #heading>
                    <!-- Title -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="flex items-center gap-3 min-w-0 flex-1">
                            <h2 class="text-xl font-semibold text-gray-800 dark:text-white">
                                Featured Image Information
                            </h2>
                        </div>
                        <t-button
                            variant="text"
                            class="hover:text-red-500"
                            @click="rightSidebar = false"
                        >
                            ✕ Close
                        </t-button>
                    </div>
                </template>
            </RightSidebar>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '../../Layouts/AppLayout.vue';
    import Button from '../../Components/Button.vue';
    import CreateIcon from '../../Components/Icons/CreateIcon.vue';
    import RightSidebar from '../../Components/RightSidebar.vue';
    import EditIcon from '../../Components/Icons/EditIcon.vue';
    import DeleteIcon from '../../Components/Icons/DeleteIcon.vue';

    export default {
        name: 'FeaturedImages',
        components: {
            AppLayout,
            CreateIcon,
            Button,
            RightSidebar,
            EditIcon,
            DeleteIcon,
        },
        props: {
            featuredImages: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                sidebarTitle: '',
                rightSidebar: false,
                rightSidebarComponentName: 'CreateFeaturedImage',
                editData: null,
            };
        },

        watch: {
            rightSidebar(newVal) {
                if (newVal === false) {
                    this.editData = null;
                }
            },

            featuredImages: {
                deep: true,
                handler() {
                    if (this.editData) {
                        this.editData = this.featuredImages.find(
                            (featuredImage) => featuredImage.id === this.editData.id,
                        );
                    }
                },
            },
        },

        methods: {
            toggleRightSidebar() {
                this.rightSidebar = !this.rightSidebar;
            },

            edit(featuredImageId) {
                const featuredImage = this.featuredImages.find((item) => item.id === featuredImageId);
                this.editData = featuredImage;
                this.toggleRightSidebar();
            },

            deleteItem(featuredImageId) {
                const featuredImage = this.featuredImages.find((item) => item.id === featuredImageId);
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to delete ${featuredImage.name}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.delete(route('featured.destroy', featuredImage.id));
                        }
                    });
            },

            togglePublication(featuredImage) {
                this.$inertia.get(route('featured.toggle', featuredImage.id));
            },
        },
    };
</script>
